// Selection and command-input DECISIONS. Pure functions over plain data: no
// DOM, no Three.js, no renderer, no world mutation. src/ui/input.js is a thin
// event adapter that projects entities to screen space, calls in here, and posts
// whatever comes back to world.issueNow().
//
// Everything a critic can accuse of feeling "mushy" is decided in this file:
//
//   * CLICK vs DRAG. A press-move-release under CLICK_SLOP_PX is a CLICK. This
//     single threshold is the number one cause of misfiring selection: set it to
//     zero and every click with a twitchy hand becomes an empty box that wipes
//     the selection; set it to 20 and small boxes stop working.
//
//   * PICKING IN SCREEN SPACE, AGAINST A COLUMN. An entity is not a point on the
//     ground; it is a vertical column from the z=0 plane up through the top of
//     its model. Hit-testing that column instead of a single projected point is
//     what makes clicking a unit on high ground work: the camera is pitched 55
//     degrees, so a unit standing 1.7 units up renders ~27 px above the spot on
//     the z=0 plane directly under it. Test only the model and a click aimed at
//     the ground under it misses; test only the ground point and a click on the
//     visible model misses. The column covers both, and "nearest column wins"
//     still resolves a crowd to exactly one unit.
//
//   * PRIORITY. Own units beat enemy units beat own buildings beat enemy
//     buildings beat resources, and only among candidates the click actually
//     touched. Mixing your units and the enemy's in one selection is impossible
//     by construction.
//
//   * MILITARY OVER WORKERS. A box holding both takes only the military. Players
//     rely on this constantly -- dragging over your own base to grab the army
//     must not drag half the mineral line along with it.

import { UnitDefs, BuildingDefs } from '../sim/defs.js';
import { SELECTION_LIMIT, MAX_SUPPLY } from '../sim/constants.js';
import { commandCard, findByHotkey } from './commandcard.js';

// --- tuning -----------------------------------------------------------------

/** Press-to-release travel under this many pixels is a click, not a box. */
export const CLICK_SLOP_PX = 5;
/** Two clicks on the same thing inside this window are a double-click. */
export const DOUBLE_CLICK_MS = 350;
/** Two taps of the same control group key inside this window centre the camera. */
export const GROUP_TAP_MS = 400;
/** Floor on a unit's pick radius so tiny units stay clickable when zoomed out. */
export const MIN_UNIT_PICK_PX = 14;
/** Floor on a building's pick radius. */
export const MIN_BUILDING_PICK_PX = 16;

// --- small helpers ----------------------------------------------------------

export const isWorker = (e) => !!(e && e.kind === 'unit' && UnitDefs[e.defId] && UnitDefs[e.defId].isWorker);
export const isMilitary = (e) => !!(e && e.kind === 'unit' && UnitDefs[e.defId] && !UnitDefs[e.defId].isWorker);
export const isBuilder = (e) => !!(e && e.kind === 'unit' && UnitDefs[e.defId] && UnitDefs[e.defId].canBuild);
export const isTownHall = (e) => !!(e && e.kind === 'building' && BuildingDefs[e.defId] && BuildingDefs[e.defId].isTownHall);
export const isRefinery = (e) => !!(e && e.kind === 'building' && e.defId === 'refinery');
/** A building that trains something has a rally point worth setting. */
export const canRally = (e) => !!(e && e.kind === 'building' && BuildingDefs[e.defId] && BuildingDefs[e.defId].produces.length > 0);

/** Normalise two corners into { x0, y0, x1, y1 } with x0<=x1, y0<=y1. */
export function normRect(x0, y0, x1, y1) {
  return {
    x0: Math.min(x0, x1), y0: Math.min(y0, y1),
    x1: Math.max(x0, x1), y1: Math.max(y0, y1),
  };
}

/** True when a press/release pair travelled far enough to mean "box", not "click". */
export function isDragGesture(x0, y0, x1, y1, slop = CLICK_SLOP_PX) {
  return Math.abs(x1 - x0) >= slop || Math.abs(y1 - y0) >= slop;
}

/** Distance from a point to a line segment, in whatever units go in. */
export function pointSegmentDistance(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 <= 1e-9) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}

/** True when segment AB touches the axis-aligned rect (endpoints inside count). */
export function segmentIntersectsRect(ax, ay, bx, by, r) {
  const inside = (x, y) => x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1;
  if (inside(ax, ay) || inside(bx, by)) return true;
  // Both endpoints outside: only a crossing can still intersect.
  const segsCross = (p0x, p0y, p1x, p1y, q0x, q0y, q1x, q1y) => {
    const d = (ux, uy, vx, vy) => ux * vy - uy * vx;
    const r1x = p1x - p0x, r1y = p1y - p0y;
    const s1x = q1x - q0x, s1y = q1y - q0y;
    const den = d(r1x, r1y, s1x, s1y);
    if (Math.abs(den) < 1e-12) return false;
    const t = d(q0x - p0x, q0y - p0y, s1x, s1y) / den;
    const u = d(q0x - p0x, q0y - p0y, r1x, r1y) / den;
    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
  };
  return segsCross(ax, ay, bx, by, r.x0, r.y0, r.x1, r.y0)
      || segsCross(ax, ay, bx, by, r.x1, r.y0, r.x1, r.y1)
      || segsCross(ax, ay, bx, by, r.x1, r.y1, r.x0, r.y1)
      || segsCross(ax, ay, bx, by, r.x0, r.y1, r.x0, r.y0);
}

// --- picking ----------------------------------------------------------------
//
// A "pick" is the screen-space footprint of one entity, built by input.js:
//   { e, bx, by, tx, ty, rx, ry, bodyRx, bodyRy, onScreen }
// (bx,by) is the entity projected at elevation 0, (tx,ty) the top of its model,
// rx/ry the pick radii in pixels -- which include a minimum so small units stay
// clickable -- and bodyRx/bodyRy the same radii WITHOUT that minimum: how big
// the thing actually draws. Everything below is arithmetic on those.

/**
 * How far the cursor is from a pick, in "radii": <= 1 means the cursor is on it.
 * Normalising by radius is what lets a 4x4 Command Center and a worker compete
 * fairly -- otherwise the building's sheer size wins every contest near it.
 */
export function pickScore(p, mx, my) {
  const d = pointSegmentDistance(mx, my, p.bx, p.by, p.tx, p.ty);
  const r = Math.max(1e-3, (p.rx + p.ry) / 2);
  return d / r;
}

/**
 * The same measurement against the entity's DRAWN size rather than its pick
 * radius -- "is the cursor on it", as opposed to "is the cursor near enough".
 *
 * A pick that carries no body radius (the pure tests build them by hand) falls
 * back to the pick radius, so for those the two questions are the same one and
 * nothing below changes behaviour.
 */
export function bodyScore(p, mx, my) {
  const d = pointSegmentDistance(mx, my, p.bx, p.by, p.tx, p.ty);
  const rx = p.bodyRx === undefined ? p.rx : p.bodyRx;
  const ry = p.bodyRy === undefined ? p.ry : p.bodyRy;
  return d / Math.max(1e-3, (rx + ry) / 2);
}

/**
 * How far into a building the cursor has to be before "it is on that building"
 * stops being arguable, in pickScore units (1.0 is the rim of its pick column).
 * 0.6 is comfortably inside the walls and nowhere near the edge a unit can
 * legitimately be standing against.
 */
export const FOOTPRINT_INNER = 0.6;

/** Own units first, then enemy units, then own buildings, enemy, resources. */
export function pickClass(e, player) {
  if (!e) return 9;
  if (e.kind === 'unit') return e.owner === player ? 0 : 1;
  if (e.kind === 'building') return e.owner === player ? 2 : 3;
  return 4;
}

/**
 * The single entity under the cursor, or null. Ties inside a class go to the
 * nearest; a lower class always beats a higher one so a worker standing on your
 * Command Center is what you get when you click the worker.
 *
 * ONE EXCEPTION, and it exists because MIN_UNIT_PICK_PX is a lie told for a
 * good reason. A unit's pick radius has a pixel floor so that a worker is still
 * clickable when the camera is all the way out -- at which point the floor is
 * nearly twice the width the worker actually draws. That surplus is harmless
 * over open ground, where there is nothing else to take. Over a BUILDING it is
 * not: the halo of a unit walking past reaches in through the wall, and a click
 * aimed squarely at a Barracks comes back holding a worker. An evaluator running
 * a macro loop hit exactly that, repeatedly, and it throttled their army.
 *
 * So: units still win a contested click, which is correct -- but when the cursor
 * is WELL INSIDE a building (FOOTPRINT_INNER), a unit has to be hit on its own
 * drawn body to take it, not merely inside its forgiveness halo. A unit standing
 * against the wall, or a flyer sitting on the roof, still wins; a unit merely
 * passing by outside does not.
 */
export function hitTest(picks, mx, my, player) {
  let insideBuilding = false;
  for (let i = 0; i < picks.length && !insideBuilding; i++) {
    const p = picks[i];
    if (!p.e || p.e.alive === false || p.e.kind !== 'building') continue;
    insideBuilding = pickScore(p, mx, my) <= FOOTPRINT_INNER;
  }

  let best = null, bestClass = 99, bestScore = Infinity;
  for (let i = 0; i < picks.length; i++) {
    const p = picks[i];
    if (!p.e || p.e.alive === false) continue;
    const s = pickScore(p, mx, my);
    if (s > 1) continue;                       // cursor is not on it at all
    if (insideBuilding && p.e.kind === 'unit' && bodyScore(p, mx, my) > 1) continue;
    const c = pickClass(p.e, player);
    if (c < bestClass || (c === bestClass && s < bestScore)) {
      best = p.e; bestClass = c; bestScore = s;
    }
  }
  return best;
}

/** If the set holds any military, drop the workers. */
export function preferMilitary(ents) {
  const mil = ents.filter(isMilitary);
  return mil.length ? mil : ents;
}

/** Deterministic order + hard cap, so the HUD never reshuffles. */
export function capIds(ents, limit = SELECTION_LIMIT) {
  const ids = ents.map((e) => e.id);
  ids.sort((a, b) => a - b);
  return ids.length > limit ? ids.slice(0, limit) : ids;
}

/**
 * Every one of YOUR units touching the box. Buildings, resources and the enemy
 * are never box-selectable -- a box that swept up an enemy marine would make
 * the next right-click order ambiguous, so the rule is enforced here rather
 * than patched up afterwards.
 */
export function boxSelectCandidates(picks, rect, player) {
  const hit = [];
  for (let i = 0; i < picks.length; i++) {
    const p = picks[i];
    const e = p.e;
    if (!e || e.alive === false) continue;
    if (e.kind !== 'unit' || e.owner !== player) continue;
    if (!segmentIntersectsRect(p.bx, p.by, p.tx, p.ty, rect)) continue;
    hit.push(e);
  }
  return preferMilitary(hit);
}

export function boxSelect(picks, rect, player, limit = SELECTION_LIMIT) {
  return capIds(boxSelectCandidates(picks, rect, player), limit);
}

/**
 * Everything a box WOULD have taken if there were no cap.
 *
 * Kept separate from boxSelect() so the input layer can compare the two and
 * tell the player when the 24-unit cap ate two thirds of their army, instead
 * of quietly handing back a short list. See SELECTION_LIMIT in constants.js.
 */
export function boxSelectAll(picks, rect, player) {
  return capIds(boxSelectCandidates(picks, rect, player), Infinity);
}

/** All visible on-screen units of one type you own -- double-click / ctrl-click. */
export function sameTypeOnScreen(picks, defId, player, limit = SELECTION_LIMIT) {
  const hit = [];
  for (let i = 0; i < picks.length; i++) {
    const p = picks[i];
    const e = p.e;
    if (!e || e.alive === false || !p.onScreen) continue;
    if (e.kind !== 'unit' || e.owner !== player || e.defId !== defId) continue;
    hit.push(e);
  }
  return capIds(hit, limit);
}

// --- selection list algebra -------------------------------------------------
//
// The selection is kept as an ORDERED id array (the Set is rebuilt from it), so
// adding a unit never reshuffles the ones already in the HUD's portrait strip.

export function addIds(order, ids, limit = SELECTION_LIMIT) {
  const out = order.slice();
  for (const id of ids) {
    if (out.length >= limit) break;
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

/** Shift+click semantics: present -> remove, absent -> append. */
export function toggleIds(order, ids, limit = SELECTION_LIMIT) {
  let out = order.slice();
  for (const id of ids) {
    const i = out.indexOf(id);
    if (i >= 0) out.splice(i, 1);
    else if (out.length < limit) out.push(id);
  }
  return out;
}

/** Drop ids whose entity is gone or dead. `get` is world.get. */
export function pruneIds(order, get) {
  const out = [];
  for (const id of order) {
    const e = get(id);
    if (e && e.alive !== false) out.push(id);
  }
  return out;
}

export function centroid(ents) {
  let n = 0, x = 0, y = 0;
  for (const e of ents) { if (!e) continue; x += e.x; y += e.y; n++; }
  return n ? { x: x / n, y: y / n } : null;
}

// --- the contextual right-click --------------------------------------------

/**
 * Turn "these things are selected, that thing is under the cursor" into the
 * commands StarCraft would issue. Returns an array of command objects without
 * `player` (input.js stamps that on). Order of the branches IS the feel:
 *
 *   enemy                      -> attack
 *   mineral / geyser / refinery-> gather   (workers; anything else just moves)
 *   own damaged structure      -> repair   (workers; anything else just moves)
 *   own town hall              -> return cargo and get back on the line
 *   anything else / empty      -> move
 *
 * A production building in the selection takes a rally point from the same
 * click, and rallying onto a mineral patch is deliberately legal: the workers
 * it builds then come out mining.
 */
export function resolveRightClick({ selection, target, x, y, queued = false, player = 0 }) {
  const cmds = [];
  const units = [], buildings = [];
  for (const e of selection) {
    if (!e || e.alive === false || e.owner !== player) continue;
    if (e.kind === 'unit') units.push(e);
    else if (e.kind === 'building') buildings.push(e);
  }

  const rallyIds = buildings.filter(canRally).map((b) => b.id);
  if (rallyIds.length) {
    const t = target && (target.kind === 'resource'
      || (isRefinery(target) && target.owner === player)
      || (target.owner >= 0 && target.owner !== player)) ? target.id : null;
    cmds.push({ type: 'rally', ids: rallyIds, x, y, targetId: t });
  }

  if (!units.length) return cmds;

  const ids = units.map((e) => e.id);
  const workerIds = units.filter(isWorker).map((e) => e.id);
  const restIds = units.filter((e) => !isWorker(e)).map((e) => e.id);
  const split = (workerCmd) => {
    cmds.push(workerCmd);
    if (restIds.length) cmds.push({ type: 'move', ids: restIds, x, y, queued });
    return cmds;
  };

  if (target && target.alive !== false) {
    const hostile = target.owner >= 0 && target.owner !== player;
    if (hostile) {
      cmds.push({ type: 'attack', ids, targetId: target.id, x: target.x, y: target.y, queued });
      return cmds;
    }
    const harvestable = target.kind === 'resource'
      || (isRefinery(target) && target.owner === player && !target.constructing);
    if (harvestable && workerIds.length) {
      return split({ type: 'gather', ids: workerIds, targetId: target.id, queued });
    }
    if (target.kind === 'building' && target.owner === player) {
      if (target.hp < target.maxHp && workerIds.length) {
        return split({ type: 'repair', ids: workerIds, targetId: target.id, queued });
      }
      if (isTownHall(target) && workerIds.length) {
        // BW's "right-click your Command Center" -- drop the cargo, get back to work.
        return split({ type: 'gather', ids: workerIds, targetId: null, queued });
      }
    }
  }

  cmds.push({ type: 'move', ids, x, y, queued });
  return cmds;
}

/**
 * What a LEFT click does while a targeting cursor is armed -- A / M / P from
 * the keyboard, or any of the command card's targeted buttons (Gather, Repair,
 * Set Rally) which arm the same state. Attack-move onto a hostile becomes a
 * direct attack order, which is what makes a-click micro feel right.
 *
 * Returns [] when the click cannot satisfy the pending command (Repair onto
 * bare ground); the caller flashes and keeps the cursor armed.
 */
export function resolveTargetedClick(cursor, { selection, target, x, y, queued = false, player = 0 }) {
  const units = [], buildings = [];
  for (const e of selection) {
    if (!e || e.alive === false || e.owner !== player) continue;
    if (e.kind === 'unit') units.push(e);
    else if (e.kind === 'building') buildings.push(e);
  }
  const ids = units.map((e) => e.id);
  const workerIds = units.filter(isWorker).map((e) => e.id);
  const restIds = units.filter((e) => !isWorker(e)).map((e) => e.id);
  const hostile = !!(target && target.owner >= 0 && target.owner !== player);

  switch (cursor) {
    case 'rally': {
      const rallyIds = buildings.filter(canRally).map((b) => b.id);
      if (!rallyIds.length) return [];
      const harvest = target && (target.kind === 'resource' || (isRefinery(target) && target.owner === player));
      return [{ type: 'rally', ids: rallyIds, x, y, targetId: harvest || hostile ? target.id : null }];
    }
    case 'gather': {
      if (!workerIds.length) return [];
      const harvest = target && (target.kind === 'resource'
        || (isRefinery(target) && target.owner === player && !target.constructing));
      const out = [{ type: 'gather', ids: workerIds, targetId: harvest ? target.id : null, queued }];
      if (restIds.length) out.push({ type: 'move', ids: restIds, x, y, queued });
      return out;
    }
    case 'repair': {
      if (!workerIds.length) return [];
      if (!target || target.owner !== player || target.hp >= target.maxHp) return [];
      return [{ type: 'repair', ids: workerIds, targetId: target.id, queued }];
    }
    default: break;
  }

  if (!ids.length) return [];
  if ((cursor === 'attackmove' || cursor === 'attack') && hostile) {
    return [{ type: 'attack', ids, targetId: target.id, x: target.x, y: target.y, queued }];
  }
  const type = cursor === 'attackmove' || cursor === 'attack' ? 'attackmove'
    : cursor === 'patrol' ? 'patrol' : 'move';
  return [{ type, ids, x, y, queued }];
}

/** Back-compat alias: the keyboard cursors are the simple case of the above. */
export function resolveCursorClick(cursor, ctx) { return resolveTargetedClick(cursor, ctx); }

// --- the hotkey table -------------------------------------------------------

/** lowercase build hotkey -> building id, straight out of defs.js. */
export const BUILD_KEYS = (() => {
  const m = {};
  for (const b of Object.values(BuildingDefs)) {
    const k = String(b.hotkey || '').toLowerCase();
    if (k && !m[k]) m[k] = b.id;
  }
  return m;
})();

// buildingActions() used to live here: a second derivation of "what can this
// structure do, and under which key". It has been deleted rather than left
// unused, because a second table is exactly the thing that drifts -- it did
// not know that a Factory shows Siege Tech under T, and the command card in
// ./commandcard.js, which decides that, is now the only place that answers.

const DIGIT = /^[0-9]$/;

// ---------------------------------------------------------------------------
// THE KEYBOARD. One owner, one table, one precedence order.
// ---------------------------------------------------------------------------
//
// There used to be two tables -- a static one here and the command card in
// ./commandcard.js -- and two listeners, src/ui/input.js and src/ui/hud.js,
// splitting the keyboard between them by CONVENTION ("I take these four kinds
// of key, you take the rest"). Conventions are not enforceable: the HUD's own
// handover notes predicted the failure that shipped, where B opened the build
// menu and no key could then pick a structure, because the HUD answered the
// second key from a command card it had not rebuilt yet and swallowed it.
//
// So there is now exactly one table, and it is the command card -- the thing
// the player is looking at. resolveKey() computes that card FRESH on every
// keypress (commandCard() is pure and cheap), which makes a stale card
// impossible by construction, and layers the four things a card cannot express
// around it in a fixed, declared order:
//
//   0. RESERVED   digits (control groups), F1, the arrow keys. These can never
//                 collide with a card hotkey because no def in defs.js binds a
//                 digit or a named key -- asserted by a test that walks every
//                 defs table.
//   1. PLACEMENT  an armed building ghost. The build submenu stays up while it
//                 is armed, so this is the same card as layer 2.
//   2. MENU       the open build submenu. It owns EVERY letter while it is up:
//                 a bound one places that structure, anything else backs out.
//                 This is what makes B then B mean Barracks even though B is
//                 also the key that opened the menu.
//   3. CARD       the command card for the current selection.
//   4. GLOBAL     Escape when something is armed but the card has no Cancel.
//   5. CAMERA     the arrow keys, which input.js reads per frame and which are
//                 never claimed here (see rule 3 at the top of input.js).
//
// A key is claimed by exactly one layer or by none, and "none" is a decision
// the caller can see: resolveKey() returns null, and nothing else runs.

/** The layers, outermost first. Exported so a test can assert the order. */
export const KEY_LAYERS = ['reserved', 'placement', 'menu', 'card', 'global', 'camera'];

/** Keys the layers above the card reserve. No def hotkey may be one of these. */
export const RESERVED_KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
  'F1', 'F10', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

/**
 * A world in which everything is affordable and every prerequisite is met.
 *
 * commandCard() needs a world to work out WHY a button is greyed. The key
 * table does not care why -- an unaffordable Barracks is still the key B --
 * so when a caller has no world (the pure tests) the card is built against
 * this, which yields the identical hotkey -> action mapping with every button
 * lit. One table, whether or not a simulation is attached.
 */
const openPlayer = () => ({ minerals: Infinity, gas: Infinity, supplyUsed: 0, supplyMax: MAX_SUPPLY, tech: new Set() });
const OPEN_WORLD = { players: [openPlayer(), openPlayer()], hasBuilding: () => true };

/** Which page of the card a given input mode is showing. */
export function cardPageFor(mode) {
  return mode === 'build' || mode === 'placement' ? 'build' : 'root';
}

/** The command card that is live for this context -- the key table itself. */
export function cardFor(ctx = {}) {
  if (ctx.card) return ctx.card;
  return commandCard(ctx.world || OPEN_WORLD, ctx.player || 0, ctx.selection || [], cardPageFor(ctx.mode));
}

/**
 * Resolve one keypress against the single table.
 *
 * ctx: { selection, mode, player, world?, card?, armed? }
 *   armed  true when a targeting cursor is up, so Escape means "disarm" rather
 *          than falling through to the card's own Cancel button.
 *
 * Returns { layer, action, button } or null. `button` is the command-card slot
 * when the card claimed the key, so the caller can flash the exact reason a
 * greyed button gives instead of doing nothing -- the difference between
 * "this game ignores my keyboard" and "Requires Supply Depot".
 */
export function resolveKey(key, mods = {}, ctx = {}) {
  if (!key) return null;
  const k = key.length === 1 ? key.toLowerCase() : key;
  const esc = k === 'Escape' || k === 'Esc';
  const mode = ctx.mode || 'normal';
  const submenu = mode === 'build' || mode === 'placement';
  const armed = submenu || !!ctx.armed;

  // --- layer 0: reserved ---------------------------------------------------
  if (DIGIT.test(k)) {
    const slot = Number(k);
    const how = mods.ctrl || mods.meta ? 'assign' : mods.shift ? 'append' : 'recall';
    return { layer: 'reserved', action: { type: 'group', slot, mode: how }, button: null };
  }
  if (k === 'ArrowUp' || k === 'ArrowDown' || k === 'ArrowLeft' || k === 'ArrowRight') {
    return { layer: 'camera', action: { type: 'camera', key: k }, button: null };
  }
  // Escape backs out of whatever is armed BEFORE the card sees it, so that a
  // Barracks mid-queue does not cancel a unit when the player only meant to
  // drop the rally cursor they just picked up.
  if (esc && armed) {
    return { layer: mode === 'placement' ? 'placement' : submenu ? 'menu' : 'global', action: { type: 'escape' }, button: null };
  }
  // Ctrl / Cmd / Alt belong to the browser and to the control groups above.
  // Claiming Cmd+S for Stop would eat the one shortcut the player did not mean
  // for the game at all, and every card hotkey is reachable without a modifier.
  if (mods.ctrl || mods.meta || mods.alt) return null;

  // --- layers 1-3: the command card ----------------------------------------
  const card = cardFor(ctx);
  const btn = findByHotkey(card, esc ? 'Escape' : k);
  if (btn) {
    return { layer: submenu ? (mode === 'placement' ? 'placement' : 'menu') : 'card', action: btn.action, button: btn };
  }

  // --- layer 2 (cont): the submenu owns the rest of the alphabet -----------
  // Nothing below this line may claim a letter while the build menu is open.
  // An unbound letter backs out, which is BW's behaviour and, crucially, is a
  // DECISION rather than a key falling through to whatever the unit card
  // happens to have under it. Named keys (F10 pauses; the browser owns Tab)
  // are not the menu's to eat.
  if (submenu && k.length === 1) return { layer: 'menu', action: { type: 'cancelmenu', targeted: false }, button: null };

  // --- layer 4: global -----------------------------------------------------
  if (esc) return { layer: 'global', action: { type: 'escape' }, button: null };

  return null;
}

/**
 * Command-card verbs -> the action vocabulary input.js executes.
 *
 * The card says "attack"; the input layer arms a cursor called 'attackmove'.
 * Both names are load-bearing in their own file, so the translation lives in
 * exactly one place rather than being re-derived at each call site.
 */
const CURSOR_FOR = {
  move: 'move', attack: 'attackmove', attackmove: 'attackmove', patrol: 'patrol',
  gather: 'gather', repair: 'repair', rally: 'rally',
};

export function toInputAction(a) {
  if (!a || !a.type) return null;
  const cursor = CURSOR_FOR[a.type];
  if (cursor) return { type: 'cursor', cursor };
  switch (a.type) {
    case 'build': return { type: 'placement', defId: a.defId };
    case 'cancelmenu': return { type: 'escape' };
    case 'siege': case 'unsiege': return { type: 'siege' };
    case 'cloak': case 'decloak': return { type: 'cloak' };
    case 'cancel': return { type: 'cancelproduction', slot: a.slot };
    case 'train': return { type: 'train', defId: a.defId };
    case 'research': return { type: 'research', defId: a.defId };
    case 'stop': return { type: 'stop' };
    case 'hold': return { type: 'hold' };
    case 'buildmenu': return { type: 'buildmenu' };
    case 'escape': return { type: 'escape' };
    default: return { ...a };
  }
}

/**
 * Back-compatible view of the one table: the action alone, in input.js's
 * vocabulary. Everything it used to decide for itself now comes out of the
 * command card, so the card and the keyboard cannot drift apart.
 *
 * mods: { shift, ctrl, alt }
 * ctx:  { selection: entity[], mode: 'normal'|'build'|'placement', player, world? }
 */
export function resolveHotkey(key, mods = {}, ctx = {}) {
  const hit = resolveKey(key, mods, ctx);
  if (!hit || hit.layer === 'camera') return null;
  return toInputAction(hit.action);
}

/** Assign / append / recall against a control-group table. Returns new ids. */
export function applyGroup(groups, slot, mode, selectionOrder, limit = SELECTION_LIMIT) {
  const cur = groups[slot] || [];
  if (mode === 'assign') return selectionOrder.length ? selectionOrder.slice(0, limit) : cur;
  if (mode === 'append') return addIds(cur, selectionOrder, limit);
  return cur.slice(0, limit);
}
